/**
 * Vortex Inspector Page
 *
 * Direct WASM Tunnel for 6D Manifest inspection.
 * Zero-Trust, high-performance visualization using native FLUX engrams.
 */

import { useState, useEffect } from 'react';
import {
  tokens,
  Card,
  VortexInspector,
  BitmaskGrid,
  Button,
  GlassPanel,
  StatDisplay,
} from '@ekelhaft-tools/eitelkeit';
import { useAgent } from '../hooks/useAgent';

export function VortexInspectorPage() {
  const { isConnected, inspectVortex, executeFlux } = useAgent();
  const [activeVortex, setActiveVortex] = useState<string | null>(null);
  const [manifestData, setManifestData] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (!isConnected) return;

    // Start the direct WASM tunnel for BRUTAL artifacts
    let unsubscribe: (() => void) | null = null;

    const startTunnel = async () => {
      unsubscribe = await (inspectVortex as any)('brutal.vortex_stream', (data: any) => {
        setManifestData(data);
        if (data.id) setActiveVortex(data.id);
      });
    };

    startTunnel();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isConnected, inspectVortex]);

  const runValidationEngram = async () => {
    setIsValidating(true);
    try {
      // Trigger the Ouroboros validation engram
      const source = `pipeline "validate_6d" { check_d1 apply_constraint "merkle_root" }`;
      await executeFlux(source);
    } catch (err) {
      console.error('Validation engram failed:', err);
    } finally {
      setIsValidating(false);
    }
  };

  if (!isConnected) {
    return (
      <div style={{ padding: tokens.spacing[6] }}>
        <h1 style={{ color: tokens.colors.mint }}>Vortex Tunnel</h1>
        <div style={{ color: tokens.colors.textMuted }}>Establishing Maelstrom session...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing[6],
        padding: tokens.spacing[4],
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: tokens.colors.mint, margin: 0 }}>Vortex Manifest Deep-Dive</h1>
        <div style={{ display: 'flex', gap: tokens.spacing[4] }}>
           <Button 
             variant="ghost" 
             onClick={runValidationEngram} 
             loading={isValidating} 
             size="md"
             leftIcon={null}
             rightIcon={null}
           >
             Run Ouroboros Validation
           </Button>
           <Button 
             variant="ghost" 
             size="md"
             loading={false}
             leftIcon={null}
             rightIcon={null}
           >
             Export Spec v0.1
           </Button>
        </div>
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: tokens.spacing[4] }}
      >
        {/* The 6D Inspector Panel */}
        <div style={{ gridColumn: 'span 2' }}>
          <VortexInspector
            vortexId={activeVortex ?? 'Awaiting data...'}
            state={manifestData ? 'running' : 'idle'}
            audioActivations={{}}
            textTokens={manifestData?.free_tags ?? []}
            dimensions={
              manifestData
                ? {
                    d1_content: manifestData.content_hash,
                    d2_semantic: manifestData.domain_flags,
                    d3_temporal: manifestData.created_at_us,
                    d4_ownership: manifestData.author_pubkey,
                    d5_encryption: manifestData.encryption_state,
                    d6_spatial: manifestData.device_id,
                  }
                : null
            }
          />
        </div>

        {/* The NAFP Bitmask Visualizer */}
        <Card variant="glass" title="D2: Semantic Resonance" padding="md" hover={true}>
          <div style={{ height: '300px', width: '100%' }}>
            {manifestData?.semantic_bitmask ? (
              <BitmaskGrid
                data={new Uint8Array(manifestData.semantic_bitmask)}
                rows={64}
                cols={64}
              />
            ) : (
              <div
                style={{ color: tokens.colors.textMuted, textAlign: 'center', paddingTop: '100px' }}
              >
                No bitmask data in stream.
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Profiling / Hot-Path Data */}
      <GlassPanel
        intensity="medium"
        blur="md"
        bordered={true}
        style={{ padding: tokens.spacing[4] }}
      >
        <h3 style={{ color: tokens.colors.mint, marginTop: 0 }}>Ouroboros Runtime Profiling</h3>
        <div style={{ display: 'flex', gap: tokens.spacing[8] }}>
          <StatDisplay
            label="Hot Path Confidence"
            value="98.2%"
            trend="up"
            trendValue="2%"
            size="md"
          />
          <StatDisplay
            label="SIMD Utilization"
            value="v128_optimized"
            trend={null}
            trendValue={null}
            size="md"
          />
          <StatDisplay
            label="Memory Pressure"
            value="O(1)_stable"
            trend={null}
            trendValue={null}
            size="md"
          />
        </div>
      </GlassPanel>

      {/* Manifest Raw Data */}
      <Card
        title="Raw 6D Substrate (Decrypted in WASM)"
        variant="default"
        padding="lg"
        hover={false}
      >
        <pre
          style={{
            fontFamily: tokens.fonts.mono,
            fontSize: tokens.fontSizes.xs,
            color: tokens.colors.mint,
            overflow: 'auto',
            maxHeight: '400px',
          }}
        >
          {JSON.stringify(manifestData, null, 2)}
        </pre>
      </Card>
    </div>
  );
}
